import { Suspense, useEffect, useState } from "react";
import PageLoader from "@/components/PageLoader";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Columns3, Table2, FolderKanban, Users, LogOut, CalendarDays, ScrollText, KeyRound, FileText, GanttChartSquare, Lightbulb, Lock, CreditCard, Sparkles, Gauge, ListChecks, ClipboardCheck, Megaphone, Activity } from "lucide-react";
import api from "@/lib/api";
import NotificationBell from "@/components/tracker/NotificationBell";
import MyPasswordDialog from "@/components/tracker/MyPasswordDialog";
import WorkspaceSwitcher from "@/components/tracker/WorkspaceSwitcher";
import { Avatar } from "@/components/tracker/Avatar";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { BillingProvider, useBilling } from "@/context/BillingContext";
import { usePresence } from "@/hooks/usePresence";
import { usePrefetchPages } from "@/hooks/usePrefetchPages";
import { useAuth } from "@/context/AuthContext";
import { AnnouncementsProvider, useAnnouncements } from "@/context/AnnouncementsContext";
import { AnnouncementPopup, AnnouncementBanners } from "@/components/tracker/AnnouncementHost";
import { TaskWindowHost } from "@/components/tracker/TaskWindow";
import { TaskEditorHost } from "@/components/tracker/TaskEditor";
import { TodoReminderHost } from "@/components/tracker/TodoReminderHost";
import { SetupDot } from "@/components/tracker/SetupGuide";
import { InviteInbox } from "@/components/tracker/InviteInbox";
import { useSetupStatus, refreshSetup } from "@/hooks/useSetupStatus";

const NAV_ITEMS = [
  { to: "/app/my-work", label: "My Work", icon: ListChecks, testid: "side-nav-my-work" },
  { to: "/app/review", label: "To review", icon: ClipboardCheck, feature: "review", testid: "side-nav-review" },
  { to: "/app/announcements", label: "Announcements", icon: Megaphone, feature: "announcements", testid: "side-nav-announcements" },
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "side-nav-dashboard" },
  { to: "/app/team", label: "Team", icon: Users, admin: true, testid: "side-nav-team" },
  { to: "/app/projects", label: "Projects", icon: FolderKanban, testid: "side-nav-projects" },
  { to: "/app/portfolio", label: "Portfolio", icon: Gauge, admin: true, testid: "side-nav-portfolio" },
  { to: "/app/board", label: "Board", icon: Columns3, testid: "side-nav-board" },
  { to: "/app/tasks", label: "Tasks", icon: Table2, testid: "side-nav-tasks" },
  { to: "/app/timeline", label: "Timeline", icon: GanttChartSquare, feature: "timeline", testid: "side-nav-timeline" },
  { to: "/app/mindmap", label: "Mind map", icon: Lightbulb, feature: "mindmap", testid: "side-nav-mindmap" },
  { to: "/app/pto", label: "Time Off", icon: CalendarDays, feature: "pto", testid: "side-nav-pto" },
  { to: "/app/audit", label: "Audit", icon: ScrollText, admin: true, feature: "audit", testid: "side-nav-audit" },
  { to: "/app/reports", label: "Reports", icon: FileText, admin: true, feature: "reports", testid: "side-nav-reports" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, owner: true, testid: "side-nav-billing" },
  { to: "/app/owner", label: "Owner console", icon: Activity, platform: true, testid: "side-nav-owner-console" },
];

function TrialBanner() {
  const { sub } = useBilling();
  const { user } = useAuth();
  if (!sub || !user?.is_owner) return null;
  if (sub.status === "trialing") {
    return (
      <Link
        to="/app/billing"
        className="no-print mb-6 flex items-center gap-3 rounded-2xl border border-[#00FF66]/25 bg-[#00FF66]/[0.06] px-5 py-3 text-sm transition-colors duration-200 hover:border-[#00FF66]/50"
        data-testid="trial-banner"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-[#00FF66]" />
        <span className="text-white/70">
          <span className="font-bold text-[#00FF66]">{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left</span> of your free trial — full access to every feature.
        </span>
        <span className="ml-auto hidden shrink-0 text-xs font-bold uppercase tracking-widest text-[#00FF66] sm:block">Choose a plan →</span>
      </Link>
    );
  }
  if (sub.status === "expired") {
    return (
      <Link
        to="/app/billing"
        className="no-print mb-6 flex items-center gap-3 rounded-2xl border border-[#FF8B94]/30 bg-[#FF8B94]/[0.07] px-5 py-3 text-sm transition-colors duration-200 hover:border-[#FF8B94]/60"
        data-testid="trial-expired-banner"
      >
        <Lock className="h-4 w-4 shrink-0 text-[#FF8B94]" />
        <span className="text-white/70">
          Your trial has ended — you are on the <span className="font-bold text-white">Free</span> plan. Upgrade to unlock Timeline, Mind map, Time Off, Reports and Audit.
        </span>
        <span className="ml-auto hidden shrink-0 text-xs font-bold uppercase tracking-widest text-[#FF8B94] sm:block">Upgrade →</span>
      </Link>
    );
  }
  return null;
}

function Shell() {
  const { user, logout } = useAuth();
  const { has, sub } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [overdue, setOverdue] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const { unread: annUnread } = useAnnouncements();
  usePresence(!!user);
  usePrefetchPages(!!user);
  const setup = useSetupStatus();
  useEffect(() => { if (user?.role === "admin") refreshSetup(); }, [location.pathname, user?.role]);

  useEffect(() => {
    const fetchCount = () => {
      const tz = -new Date().getTimezoneOffset();
      api.get(`/me/overdue-count?tz_offset=${tz}`).then((res) => setOverdue(res.data.overdue || 0)).catch(() => {});
      api.get(`/me/review-count`).then((res) => setReviewCount(res.data.count || 0)).catch(() => {});
    };
    fetchCount();
    // Keep the sidebar badges fresh on their own: poll periodically, and refetch
    // when the user returns to the tab (pause while hidden to avoid wasted calls).
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchCount();
    }, 30000);
    window.addEventListener("focus", fetchCount);
    document.addEventListener("visibilitychange", fetchCount);
    window.addEventListener("review:refresh", fetchCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", fetchCount);
      document.removeEventListener("visibilitychange", fetchCount);
      window.removeEventListener("review:refresh", fetchCount);
    };
  }, [location.pathname]);
  const items = NAV_ITEMS.filter(
    (item) => (!item.admin || user.role === "admin") && (!item.owner || user.is_owner) && (!item.platform || user.platform_owner),
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex shrink-0 snap-start items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
      isActive ? "bg-[#00FF66]/10 text-[#00FF66]" : "text-white/50 hover:bg-white/5 hover:text-white"
    }`;

  const nav = (item, suffix = "") => {
    const locked = item.feature && sub && !has(item.feature);
    return (
      <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} data-testid={`${item.testid}${suffix}`}>
        <item.icon className="h-4 w-4 shrink-0" />
        <span className={locked ? "text-white/30" : undefined}>{item.label}</span>
        {item.testid === "side-nav-my-work" && overdue > 0 && (
          <span
            className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF8B94] px-1.5 text-[10px] font-black text-black"
            data-testid={`my-work-badge${suffix}`}
          >
            {overdue > 99 ? "99+" : overdue}
          </span>
        )}
        {item.testid === "side-nav-review" && !locked && reviewCount > 0 && (
          <span
            className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF8B94] px-1.5 text-[10px] font-black text-black"
            data-testid={`review-badge${suffix}`}
          >
            {reviewCount > 99 ? "99+" : reviewCount}
          </span>
        )}
        {item.testid === "side-nav-announcements" && !locked && annUnread > 0 && (
          <span
            className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FF8B94] px-1.5 text-[10px] font-black text-black"
            data-testid={`announcements-badge${suffix}`}
          >
            {annUnread > 99 ? "99+" : annUnread}
          </span>
        )}
        {locked && <Lock className="ml-auto h-3 w-3 shrink-0 text-white/25" data-testid={`${item.testid}-lock${suffix}`} />}
        {item.testid === "side-nav-team" && setup && !setup.team.done && <SetupDot testid={`setup-dot-team${suffix}`} />}
        {item.testid === "side-nav-projects" && setup && !setup.projects.done && <SetupDot testid={`setup-dot-projects${suffix}`} />}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="tracker-layout">
      <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-white/10 bg-[#070907] md:flex">
        <div className="flex items-center justify-between px-6 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <span className="block h-3 w-3 rotate-45 rounded-[3px] bg-[#00FF66] shadow-[0_0_14px_rgba(0,255,102,0.8)]" />
            <div className="min-w-0">
              <span className="font-display block text-base font-extrabold leading-none tracking-[0.26em]">VEKTOR</span>
              <WorkspaceSwitcher />
            </div>
          </div>
          <NotificationBell />
        </div>
        <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto px-3 py-1">{items.map((item) => nav(item))}</nav>
        <div className="border-t border-white/10 p-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <div className="flex flex-col items-center text-center">
              <Avatar name={user.name} size={40} className={user.is_owner ? "ring-2 ring-[#00FF66]/50 ring-offset-2 ring-offset-[#070907]" : ""} />
              <p className="mt-2 w-full truncate text-sm font-semibold leading-tight" data-testid="sidebar-user-name">{user.name}</p>
              <p className="mt-0.5 w-full truncate text-xs leading-tight text-white/40" title={user.email}>{user.email}</p>
              <div className="mt-2.5 flex w-full items-center justify-center gap-2">
                <span
                  className={`inline-flex min-w-[76px] items-center justify-center rounded-full px-3 py-1 text-[10px] font-bold uppercase leading-none tracking-widest ${
                    user.role === "admin" ? "bg-[#00FF66] text-black" : "border border-white/20 text-white/50"
                  }`}
                  data-testid="sidebar-role-chip"
                >
                  {user.is_owner ? "owner" : user.role}
                </span>
                {sub && (
                  <span className="inline-flex min-w-[76px] items-center justify-center rounded-full border border-white/15 px-3 py-1 text-[10px] font-bold uppercase leading-none tracking-widest text-white/45" data-testid="sidebar-plan-chip">
                    {sub.plan_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-1.5 space-y-0.5">
            <button
              onClick={() => setPwdOpen(true)}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-white/50 transition-colors duration-200 hover:bg-white/5 hover:text-[#00FF66]"
              data-testid="change-password-button"
            >
              <KeyRound className="h-4 w-4" />
              Change password
            </button>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-white/50 transition-colors duration-200 hover:bg-white/5 hover:text-[#FF8B94]"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="no-print sticky top-0 z-40 border-b border-white/10 bg-[#070907]/90 backdrop-blur-xl md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="font-display text-sm font-extrabold tracking-[0.26em]">VEKTOR</span>
          <div className="flex items-center gap-1">
            <WorkspaceSwitcher variant="mobile" />
            <NotificationBell />
            <button onClick={() => setPwdOpen(true)} className="p-2 text-white/50" data-testid="change-password-button-mobile" aria-label="Change password">
              <KeyRound className="h-4 w-4" />
            </button>
            <button onClick={handleLogout} className="p-2 text-white/50" data-testid="logout-button-mobile" aria-label="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="relative">
          <span className="pointer-events-none absolute bottom-2 right-0 top-0 z-10 w-8 bg-gradient-to-l from-[#070907] to-transparent" aria-hidden="true" />
          <nav className="flex snap-x snap-mandatory gap-1 overflow-x-auto px-3 pb-2 pr-8">
            {items.map((item) => nav(item, "-mobile"))}
          </nav>
        </div>
      </div>

      <main className="px-5 py-8 md:ml-60 md:px-10 md:py-10">
        <TrialBanner />
        <AnnouncementBanners />
        <InviteInbox />
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </motion.div>
      </main>
      <MyPasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <AnnouncementPopup />
      <TaskWindowHost />
      <TaskEditorHost />
      <TodoReminderHost />
    </div>
  );
}

export default function TrackerLayout() {
  return (
    <NotificationsProvider>
      <BillingProvider>
        <AnnouncementsProvider>
          <Shell />
        </AnnouncementsProvider>
      </BillingProvider>
    </NotificationsProvider>
  );
}
